import { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { useParams } from "react-router";
import { useAppSelector } from "../../app/hooks";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import Avatar from "../../components/Avatar";
import StackedDate from "../../components/StackedDate";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import PromptPanel from "../prompts/PromptPanel";
import CommentedEssay from "../writing/drafting/CommentedEssay";
import { usePeerReviewIndexQuery } from "./peerReviewsApiSlice";
import { PeerReviewInteraction } from "./PeerReviewInteraction";

export const PEER_REVIEW_MAX_LENGTH = 1000;

export default function PeerReview() {
	const { userId } = useAppSelector((state) => state.user);

	const { id } = useParams();
	const peerReviewId = Number(id);

	const [view, setView] = useState<string>("react");

	const result = usePeerReviewIndexQuery(peerReviewId ?? skipToken);
	const data = result.data;
	const review = result.data?.peerReview;
	const canUpdateReview = review?.canEditPage && !review?.isReviewed;

	return (
		<section className="peerReview">
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Peer Review</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					simpleListBoxClasses="print:hidden"
				/>
			</div>
			{view == "react" && data && (
				<div className="print:mt-8">
					<div className="flex">
						<StackedDate className="ml-8 mr-24" date={data.draft.createDate} />
						<div>
							<h2>{data.prompt.title}</h2>
							<div>by {data.peerReview.author}</div>
							<div>for {data.courseName}</div>
						</div>
					</div>
					<div>
						<PromptPanel
							name={data.prompt.title}
							showName={false}
							renderedText={data.prompt.renderedText}
							stimulusMaterials={data.stimulusMaterials}
							signLanguageVideoURL={data.signLanguageVideoURL}
							showGenre={false}
							className="print:hidden"></PromptPanel>
					</div>
					<div className="xl:flex xl:space-x-4 mt-8">
						<div className="xl:w-1/2 print:hidden">
							<CommentedEssay
								commentedEssay={data.commentedEssay}
								showAnnotations={false}
							/>
						</div>
						<div className="mt-8 xl:mt-0 xl:w-1/2 ">
							{canUpdateReview && (
								<p className="mb-4 mt-8 print:hidden">
									Please complete your review of{" "}
									{data.peerReview.canShowEssayAuthor
										? data.peerReview.author + "’s "
										: "the"}{" "}
									essay. Fill in all fields for submission.
								</p>
							)}
							{!canUpdateReview && data.peerReview.reviewerId != userId && (
								<div className="mt-4 flex place-items-center">
									<div className="mr-4">Review by {data.peerReview.reviewer}</div>
									<Avatar
										className="print:hidden"
										hasImage={data.peerReview.reviewerHasImage}
										initials={data.peerReview.reviewerInitials ?? "?"}
										userId={data.peerReview.reviewerId}
									/>
								</div>
							)}

							<PeerReviewInteraction
								className="mt-4"
								draftId={data.draft.id}
								review={data.peerReview}
								readOnly={!canUpdateReview}
								showPrint={true}
							/>
						</div>
					</div>
				</div>
			)}

			{view == "raw" && <RtkqResultViewer heading="Peer Review Data" result={result} />}
		</section>
	);
}
